import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Tag from "@/components/base/Tag";
import { useFavorites } from "@/hooks/useFavorites";
import {
  detailPath,
  formatDate,
  getById,
  getRelated,
  isContentType,
  shareTag,
  typeMeta,
} from "@/lib/content";
import type { ContentType } from "@/mocks/content";

export default function DetailPage() {
  const { type: rawType, id } = useParams<{ type: string; id: string }>();
  const type: ContentType | null = rawType && isContentType(rawType) ? rawType : null;
  const item = type && id ? getById(type, id) : undefined;

  const { isFavorite, toggleFavorite } = useFavorites();
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    setShowShare(false);
    setCopied(false);
  }, [type, id]);

  if (!item || !type) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 pt-28 pb-20 text-center md:px-8 md:pt-36">
          <i className="ri-file-forbid-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            内容不存在或已下线
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            该内容可能已被移除，请返回栏目浏览其他文章。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-home-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const meta = typeMeta[type];
  const favKey = shareTag(item);
  const favorite = isFavorite(favKey);
  const related = getRelated(item, 3);
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const scrollToSection = (index: number) => {
    const el = document.getElementById(`section-${index}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTocOpen(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-secondary-50">
        <div className="mx-auto w-full max-w-7xl px-4 pt-28 pb-10 md:px-8 md:pt-36 md:pb-14">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: meta.label, to: meta.path },
              { label: item.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Tag tone="accent">{meta.short}</Tag>
            {item.group && <Tag tone="primary">{item.group}</Tag>}
            {item.series && <Tag>{item.series}</Tag>}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {item.subtitle}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true" />
              {formatDate(item.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" aria-hidden="true" />
              {item.readTime}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0">
            <div className="mb-8 h-56 w-full overflow-hidden rounded-lg bg-background-100 md:h-80">
              <img
                src={item.cover}
                alt={`${item.title} 主题封面`}
                title={`${item.title} - 家庭教师动画平台`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mb-8 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
                <strong className="font-bold text-foreground-950">内容摘要：</strong>
                {item.summary}
              </p>
            </div>

            {item.sections.map((section, index) => (
              <section
                key={section.heading}
                id={`section-${index}`}
                className="mb-9 scroll-mt-24"
              >
                <h2 className="mb-4 flex items-center gap-3 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-xs font-bold text-primary-700">
                    {index + 1}
                  </span>
                  {section.heading}
                </h2>
                <div className="space-y-4">
                  {section.body.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            <div className="flex flex-wrap gap-2 border-t border-background-200 pt-6">
              {item.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </div>

            <p className="mt-6 rounded-md bg-secondary-50 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              本站内容为演示虚拟数据，仅用于展示信息结构与浏览体验，不涉及真实版权信息或评分。
            </p>
          </article>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-list-check text-primary-600" aria-hidden="true" />
                章节目录
              </h2>
              <ul className="mt-4 space-y-2">
                {item.sections.map((section, index) => (
                  <li key={section.heading}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(index)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className="text-xs font-bold text-accent-500">{index + 1}</span>
                      {section.heading}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5">
              <button
                type="button"
                onClick={() => toggleFavorite(favKey)}
                className={`flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  favorite
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                }`}
              >
                <i
                  className={favorite ? "ri-heart-fill" : "ri-heart-line"}
                  aria-hidden="true"
                />
                {favorite ? "已收藏（本地）" : "收藏本文"}
              </button>
              <button
                type="button"
                onClick={() => setShowShare(true)}
                className="flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
              >
                <i className="ri-share-forward-line" aria-hidden="true" />
                分享本文
              </button>
              <p className="text-center text-xs text-foreground-400">
                收藏仅保存在当前浏览器，无需登录
              </p>
            </div>
          </aside>
        </div>

        <section className="mt-14">
          <h2 className="mb-6 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
            <i className="ri-links-line text-accent-500" aria-hidden="true" />
            相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((rec) => (
              <Link
                key={rec.id}
                to={detailPath(rec)}
                className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                  <img
                    src={rec.cover}
                    alt={`${rec.title} 封面图`}
                    title={`${rec.title} - 家庭教师动画平台`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <span className="text-xs text-accent-600">{typeMeta[rec.type].short}</span>
                  <h3 className="mt-1 line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                    {rec.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {showShare && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭分享弹窗"
            onClick={() => setShowShare(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="relative w-full max-w-sm rounded-lg bg-background-50 p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">分享本文</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShowShare(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <p className="mt-2 text-xs text-foreground-500">
              使用微信「扫一扫」即可在手机上打开本文
            </p>
            <div className="mt-4 flex flex-col items-center">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(currentUrl)}`}
                alt="分享二维码"
                className="h-44 w-44 rounded-md border border-background-200"
              />
            </div>
            <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
              <i className="ri-link text-foreground-400" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-xs text-foreground-600">
                {currentUrl}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="shrink-0 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                {copied ? "已复制" : "复制链接"}
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}